<template>
  <WorkBaseRestrictedTextField
    v-bind="$props"
    ref="restrictedTextField"
    class="w-currency-field"
    :value="value"
    :label="formatLabel"
    :char-whitelist="internalCharWhiteList"
    :always-allowed-keys="internalAlwaysAllowedKeys"
    :placeholder="internalPlaceholder"
    :input-get-formatter="invokeGetFormatter"
    :input-set-formatter="invokeSetFormatter"
    :format-value-on-focus-out="invokeFormatOnFocusOut"
    :format-value-on-focus-in="invokeFormatOnFocusIn"
    :read-only-formatter="invokeReadonlyFormat"
    :align-right="alignRight"
    :next-to-each-other="nextToEachOther"
    @input="$emit('input', $event)"
  >
    <template
      v-if="withCurrencySwitch"
      #prepend-inner
    >
      <w-settings-button
        small
        @click="openDialog"
      />
      <WorkCurrencyDialog ref="currencyDialog" />
    </template>
  </WorkBaseRestrictedTextField>
</template>

<script>
// components
import WorkBaseRestrictedTextField from '@/components/global_components/forms/text_fields/WorkBaseRestrictedTextField';
import WorkCurrencyDialog from './dialogs/WorkCurrencyDialog'
// mixins
import numberFieldMixin from '@/components/global_components/forms/text_fields/mixins/number-field-mixin';
import isRequired from '@/mixins/component/is-required-mixin';
import pendingAble from '@/mixins/component/pending-able-mixin';
// helpers
import {
  formatCurrencyToNumber,
  formatNumberToCurrency,
  getSymbol
} from '@/services/formatter_services/currency-formatter';
import { CURRENCY_TYPE_CONFIG } from '@/services/formatter_services/enums/CURRENCY_TYPE_CONFIG';
import staticData from "@/mixins/api_data/static-data-mixin";

export default {
  name: 'WorkCurrencyTextField',
  components: { WorkBaseRestrictedTextField, WorkCurrencyDialog },
  props: {
    value: { type: [Number, String] },
    currency: {
      type: Object,
      default: () => {
        return { data: { symbol: 'EUR' } };
      }
    },
    // feature flags
    withCurrencySwitch: { type: Boolean, default: false },
    alignRight: { type: Boolean, default: true },
    labelWithSymbol: { type: Boolean, default: false },
    nextToEachOther: { type: Boolean, default: false },
    spaceBetween: { type: Boolean, default: false }
  },
  mixins: [
    staticData('defaultCurrencyType',
      self =>
        self.currency?.id
      ? `system/currencies/${self.currency.id}`
      : `system/countries/default`
    ),
    isRequired,
    pendingAble,
    numberFieldMixin(
      {
        getFormatter: (v, self) =>
          formatNumberToCurrency(v, self.currencyType),
        setFormatter: (v, self) => formatCurrencyToNumber(v, self.currencyType),
        formatOnFocusIn: (v, self) =>
          formatNumberToCurrency(v, self.currencyType, false),
        formatOnFocusOut: (v, self) =>
          formatNumberToCurrency(v, self.currencyType),
        formatToReadonly: (v, self) =>  formatNumberToCurrency(v, self.currencyType)
      },
      true
    ),
  ],
  data() {
    return {
      CURRENCY_TYPE_CONFIG: Object.entries(CURRENCY_TYPE_CONFIG),
    };
  },
  computed: {
    currencyType(){
      return this.currency?.data?.symbol ?? this.defaultCurrencyType?.symbol
    },
    internalPlaceholder() {
      if (this.placeholder != undefined) {
        return this.placeholder;
      }
      return formatNumberToCurrency(0, this.currencyType);
    },
    formatLabel(){
      if (this.labelWithSymbol && this.currencyType) {
        return `${this.label} (${getSymbol(this.currencyType)})`
      }
      return this.label
    }
  },
  watch: {
    currency() {
      this.updateValueWithCurrency()
    },
  },
  methods: {
    openDialog(){
      this.$refs.currencyDialog.open().then((response) => {
        this.changeCurrencyType(response)
      });
    },
    async changeCurrencyType(currency) {
      await this.$emit('update:currency', currency);
    },
    updateValueWithCurrency(){
      let newValueFormat = this.value;
      if (this.value != undefined) {
        newValueFormat = this.invokeFormatOnFocusOut(newValueFormat);
      }
      this.$refs.restrictedTextField.updateInternalValues(newValueFormat);
    },
    focus() {
      this.$refs.restrictedTextField.focus();
    }
  }
};
</script>

<style lang="scss" scoped>
.w-currency-field ::v-deep {
  .v-text-field__slot input {
    text-align: end !important;
  }
}
</style>
